/**
 * Expenses — typing centre list (UI-1 v3 layout, BE-5a/b/c data)
 *
 * Route: /accounting/expenses (?view=templates shows the monthly templates)
 * Search, category and status filters run in the database; "Pending Review" lists the monthly drafts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  IconButton, Chip, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Grid, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert, Tooltip, useTheme,
} from '@mui/material';
import { Add, Edit, Delete, Search, Refresh, Repeat, Category as CategoryIcon, ArrowBack } from '@mui/icons-material';
import { StatusChip } from '../../components/accounting';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';
import { expenseService } from '../../services/accounting/expense.service';
import { serverMessage } from '../../utils/apiConvert';
import ExpenseTemplates from './ExpenseTemplates';

const fontSize = { sm: '0.875rem', xs: '0.75rem', md: '1rem' };
const STATUSES = [
  ['PENDING_REVIEW', 'Pending Review'],
  ['PENDING_APPROVAL', 'Pending Approval'],
  ['APPROVED', 'Approved'],
  ['REJECTED', 'Rejected'],
  ['REVERSED', 'Reversed'],
];
const headSx = { fontSize: fontSize.xs, fontWeight: 600 };

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const ExpenseList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showTemplates = searchParams.get('view') === 'templates';
  const user = sessionUser();
  const canWrite = can(user, 'finance', 'write');
  const canDelete = can(user, 'finance', 'delete');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [total, setTotal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const q = useDebounced(searchTerm.trim(), 350);

  const filters = useCallback(() => ({
    q: q || undefined,
    categoryId: categoryFilter || undefined,
    status: statusFilter && statusFilter !== 'PENDING_REVIEW' ? statusFilter : undefined,
  }), [q, categoryFilter, statusFilter]);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filters(), page, size: rowsPerPage };
      const [rows, sum] = await Promise.all([
        statusFilter === 'PENDING_REVIEW'
          ? expenseService.pendingReview({ page, size: rowsPerPage })
          : expenseService.list(params),
        expenseService.total(statusFilter === 'PENDING_REVIEW' ? { status: 'DRAFT' } : filters()).catch(() => null),
      ]);
      setData(rows);
      setTotal(sum);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, statusFilter]);

  useEffect(() => { if (!showTemplates) loadExpenses(); }, [loadExpenses, showTemplates]);
  useEffect(() => { expenseService.getCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => { setPage(0); }, [q, categoryFilter, statusFilter]);
  useEffect(() => {
    if (!success && !error) return undefined;
    const t = setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    return () => clearTimeout(t);
  }, [success, error]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await expenseService.remove(toDelete.id);
      setSuccess('Expense deleted successfully');
      setToDelete(null);
      loadExpenses();
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete expense'));
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  if (showTemplates) {
    return (
      <Box sx={{ width: 0, minWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button size="small" startIcon={<ArrowBack />} onClick={() => setSearchParams({})} sx={{ textTransform: 'none' }}>Expenses</Button>
          <Typography variant="h5" fontWeight={600} color="secondary.main">Monthly Templates</Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <ExpenseTemplates categories={categories} onError={setError} canWrite={canWrite} />
      </Box>
    );
  }

  const rows = data.content;

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={600} color="secondary.main">Expenses</Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Manage operational expenses</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadExpenses} size="small"><Refresh fontSize="small" /></IconButton>
          </Tooltip>
          <Button size="small" startIcon={<Repeat />} onClick={() => setSearchParams({ view: 'templates' })} sx={{ textTransform: 'none' }}>
            Monthly Templates
          </Button>
          <Button size="small" startIcon={<CategoryIcon />} onClick={() => navigate('/accounting/expenses?tab=categories')} sx={{ textTransform: 'none' }}>
            Categories
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => navigate('/accounting/expenses/new')}>
              Add Expense
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField placeholder="Search expenses..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              size="small" fullWidth disabled={statusFilter === 'PENDING_REVIEW'}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth disabled={statusFilter === 'PENDING_REVIEW'}>
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="">All Categories</MenuItem>
                {categories.filter((c) => c.active !== false).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All Statuses</MenuItem>
                {STATUSES.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, textAlign: 'right' }}>
              Total: {total == null ? '—' : formatCurrency(total)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={headSx}>Date</TableCell>
                  <TableCell sx={headSx}>Category</TableCell>
                  <TableCell sx={headSx}>Description</TableCell>
                  <TableCell sx={headSx}>Payee</TableCell>
                  <TableCell sx={headSx} align="right">Amount</TableCell>
                  <TableCell sx={headSx} align="center">Status</TableCell>
                  <TableCell sx={headSx} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', py: 4 }}>No expenses found</Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.map((expense) => {
                  const recurring = expense.sourceType === 'RECURRING_TEMPLATE';
                  const fine = expense.sourceType === 'WORK_ORDER_FINE' || expense.sourceType === 'PARTNER_FINE';
                  const unpaid = expense.expenseType === 'PAYABLE' && expense.status === 'APPROVED' && !expense.paidAt;
                  const editable = canWrite && !fine && (expense.status === 'DRAFT' || expense.status === 'PENDING_APPROVAL');
                  return (
                    <TableRow key={expense.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/accounting/expenses/${expense.id}`)}>
                      <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{formatDate(expense.date)}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: fontSize.sm }}>{expense.category?.name || (expense.expenseType === 'RETURN' ? 'Refund' : '—')}</Typography>
                        {expense.category?.group?.name && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{expense.category.group.name}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ fontSize: fontSize.sm }}>{expense.description}</Typography>
                        {recurring && expense.status === 'DRAFT' ? (
                          <Chip label="Recurring — Review Required" size="small"
                            sx={{ fontSize: '0.65rem', height: 20, ml: 1, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                        ) : recurring ? (
                          <Chip label="Recurring" size="small" sx={{ fontSize: '0.65rem', height: 18, ml: 1, bgcolor: 'action.selected' }} />
                        ) : null}
                        {fine && <Chip label="Work order fine" size="small" sx={{ fontSize: '0.65rem', height: 18, ml: 1, bgcolor: 'action.selected' }} />}
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{expense.expenseNumber}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>
                        {expense.payeeName || '—'}
                        {expense.salaryMonth && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            Salary for {new Date(`${String(expense.salaryMonth).slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }} align="right">
                        {expense.expenseType === 'RETURN' ? '−' : ''}{formatCurrency(expense.total)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <StatusChip type="status" value={expense.status} />
                          {unpaid && <StatusChip tone="warning" label="Unpaid" />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                        {editable && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => navigate(`/accounting/expenses/${expense.id}/edit`)}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {editable && canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setToDelete(expense)} color="error"><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination component="div" count={data.totalElements} page={page} onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]} />
          </>
        )}
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)}>
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this expense?</Typography>
          {toDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{toDelete.description}</Typography>
              <Typography sx={{ fontSize: fontSize.xs }}>{formatCurrency(toDelete.total)} - {formatDate(toDelete.date)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseList;
